import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Scanner',   path: '/scanner',   color: 'text-[#4f8ff8]' },
  { label: 'Watchlist', path: '/watchlist', color: 'text-[#10d98e]' },
  { label: 'Alerts',    path: '/alerts',    color: 'text-[#a78bfa]' },
  { label: 'Events',    path: '/events',    color: 'text-amber-400'  },
];

export default function NotFoundPage() {
  const location = useLocation();
  const [dots, setDots] = useState('');

  useEffect(() => {
    const id = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-lg">

        {/* ── Main card ── */}
        <div className="relative rounded-2xl overflow-hidden border border-gray-200 dark:border-[#1d2d47] bg-white dark:bg-[#0e1624] shadow-sm">

          {/* Gradient accent strip */}
          <div className="h-[3px] bg-gradient-to-r from-[#f5476a] via-[#a78bfa] to-[#4f8ff8]" />

          {/* Background orbs */}
          <div className="pointer-events-none absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-to-br from-[#f5476a] to-[#a78bfa] opacity-[0.06] dark:opacity-[0.09] blur-3xl" />
          <div className="pointer-events-none absolute bottom-0 -left-8 w-40 h-40 rounded-full bg-[#4f8ff8] opacity-[0.05] dark:opacity-[0.07] blur-2xl" />

          <div className="relative p-8 sm:p-10">

            {/* Error code */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-[72px] font-black leading-none tracking-tighter bg-gradient-to-br from-[#f5476a] via-[#a78bfa] to-[#4f8ff8] bg-clip-text text-transparent select-none">
                404
              </span>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-mono font-bold tracking-[0.2em] text-red-400 dark:text-[#f5476a] uppercase">
                  Error
                </span>
                <span className="text-[10px] font-mono text-gray-400 dark:text-[#a3b6cc] tracking-widest">
                  PAGE_NOT_FOUND
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-[#1d2d47] to-transparent mb-6" />

            {/* Message */}
            <h1 className="text-[18px] font-bold text-gray-900 dark:text-[#f1f5fb] mb-2 tracking-tight">
              Route not found in active matrix
            </h1>
            <p className="text-[13px] text-gray-500 dark:text-[#a3b6cc] mb-1">
              The path{' '}
              <code className="px-1.5 py-0.5 rounded-md bg-gray-100 dark:bg-[#141e30] text-[#f5476a] font-mono text-[12px] border border-gray-200 dark:border-[#1d2d47]">
                {location.pathname}
              </code>{' '}
              does not exist in this workspace.
            </p>
            <p className="text-[12px] text-gray-400 dark:text-[#a3b6cc] font-mono mt-3 mb-6">
              SCANNER_CORE › RESOLVING_ROUTE{dots}
            </p>

            {/* CTA */}
            <Link
              to="/scanner"
              className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#4f8ff8] to-[#6366f1] text-white text-[13px] font-semibold shadow-sm hover:opacity-90 active:scale-[0.98] transition-all duration-200"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Return to Scanner
            </Link>
          </div>
        </div>

        {/* ── Quick nav ── */}
        <div className="mt-5 flex items-center gap-2 flex-wrap justify-center">
          <span className="text-[10.5px] font-mono text-gray-400 dark:text-[#a3b6cc] uppercase tracking-widest">
            Or jump to:
          </span>
          {NAV_LINKS.map(link => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-[12px] font-semibold ${link.color} hover:underline underline-offset-2 transition-all`}
            >
              {link.label}
            </Link>
          ))}
        </div>

      </div>
    </div>
  );
}
